import React, { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { RemoveUnavailableItemsModal } from 'components/remove-unavailable-items-modal';
import { useCart } from 'hooks/use-cart';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { isMobile } from 'utils/environment';

import { LoyaltyRewardsCategoryCarousel } from './loyalty-rewards-category-carousel';
import { LoyaltyRewardsCategoryList } from './loyalty-rewards-category-list';
import { LoyaltyRewardsMeter } from './loyalty-rewards-meter';
import {
  CategoryLabel,
  CrownIcon,
  RewardCategoryContainer,
  RewardCategoryHeader,
} from './loyalty-rewards.styled';
import { ILoyaltyRewardsCategory } from './types';
import { useAppliedReward } from './use-applied-reward';

export const LoyaltyRewardsCategory = ({
  rewards,
  category,
  firstUnavailableCategory,
  engineRewardsMap,
  handleOnPress,
}: ILoyaltyRewardsCategory) => {
  const formatters = useIntl();
  const { formatMessage } = formatters;
  const { cartEntries, removeFromCart } = useCart();
  const [unavailableCartEntry, setUnavailableCartEntry] = useState<ILegacyCartEntry | null>(null);
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const appliedReward = useAppliedReward();
  const { rewardsCooldown } = useLoyaltyContext();
  const { isAuthenticated } = useAuthContext();

  // Only show carousel if on Desktop web
  const showCarousel = !isMobile();

  const categoryLabel = category.label?.en;

  const handleRemoveUnavailableItems = useCallback(() => {
    if (unavailableCartEntry) {
      setUnavailableCartEntry(null);
      removeFromCart({ cartId: unavailableCartEntry.cartId });
    }
  }, [removeFromCart, unavailableCartEntry]);

  useEffect(() => {
    // If by any chance an applied reward that's actually unavailable is found, remove it
    if (enableRewardUnstacking && appliedReward && rewards[0]?.rewardEvaluationResults?.length) {
      const cartEntry = cartEntries.find(entry => entry.cartId === appliedReward.cartId);

      if (cartEntry) {
        setUnavailableCartEntry(cartEntry);
      }
    }
  }, [rewards, enableRewardUnstacking, appliedReward, removeFromCart, cartEntries]);

  const categoryHeaderAccessibilityHint = `${categoryLabel} ${formatMessage({
    id: 'crowns',
  })}`;

  const engineRewardItem = engineRewardsMap[rewards[0].reward.loyaltyEngineId || ''];
  const { locked, redemptionPercentage, remainingPointsNeededForRedemption } = engineRewardItem;
  const showMeter = locked && firstUnavailableCategory?.label?.en === categoryLabel;

  return (
    <>
      {showMeter ? (
        <LoyaltyRewardsMeter
          redemptionPercentage={redemptionPercentage || 0}
          remainingPointsNeededForRedemption={remainingPointsNeededForRedemption || undefined}
        ></LoyaltyRewardsMeter>
      ) : null}

      <RewardCategoryContainer locked={locked || false} accessible={false}>
        <RewardCategoryHeader>
          <CrownIcon />
          <CategoryLabel accessibilityLabel={categoryHeaderAccessibilityHint}>
            {categoryLabel}
          </CategoryLabel>
        </RewardCategoryHeader>

        {showCarousel ? (
          <LoyaltyRewardsCategoryCarousel
            rewards={rewards}
            locked={!isAuthenticated || locked || false}
            handleOnPress={handleOnPress}
            inCooldown={rewardsCooldown.isActive}
          />
        ) : (
          <LoyaltyRewardsCategoryList
            rewards={rewards}
            locked={!isAuthenticated || locked || false}
            handleOnPress={handleOnPress}
            inCooldown={rewardsCooldown.isActive}
          />
        )}
      </RewardCategoryContainer>

      {unavailableCartEntry && (
        <RemoveUnavailableItemsModal
          onConfirmCb={handleRemoveUnavailableItems}
          unavailableCartEntries={[unavailableCartEntry]}
        />
      )}
    </>
  );
};
